import L, { LatLngBounds, Map as LeafletMap } from 'leaflet';
import { Overlay } from '@x-guard/xgac-types/xgac';
import '@x-guard/xgac-leaflet-distortable-image';

const AnyL = L as any;

// Transformed overlay <img>s become GPU textures at about bitmap size × devicePixelRatio.
// Past the texture limit — or with many large ones — Chrome corrupts compositing for the
// whole page, not just the map.
// ponytail: 8192/4096 are fixed stand-ins for MAX_TEXTURE_SIZE, not read from WebGL.
const GPU_MAX_TEXTURE = 8192;
const MAX_PARALLEL_DECODES = 2;
// 1×1 gif. Used when a bitmap cannot be scaled down, so the page never falls back to the original file.
const SAFE_EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const OVERLAY_THUMB_MAX_EDGE = 800;

type CachedSize = {
  edge: number;
  url: Promise<string>;
};

type AttachOptions = {
  className: string;
  editable: boolean;
  actions: any[];
  mode?: string;
  exposeFeatureId?: boolean;
  onCreate?: (layer: any, overlay: Overlay) => void;
};

const sizedUrls = new Map<string, CachedSize[]>();
let activeDecodes = 0;
const decodeQueue: Array<() => void> = [];

const isLatLng = (point: { lat?: number; lng?: number } | null | undefined): point is { lat: number; lng: number } => (
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng)
);

export const overlayImageSource = (imageUrl: unknown): string | null => {

  if (typeof imageUrl === 'string' && imageUrl.length > 0) return imageUrl;
  if (imageUrl && typeof imageUrl === 'object' && 'src' in imageUrl) {

    const src = (imageUrl as { src?: unknown }).src;
    if (typeof src === 'string' && src.length > 0) return src;

  }
  return null;

};

const fittedSize = (
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } | null => {

  const longest = Math.max(width, height);
  if (!longest || longest <= maxEdge) return null;
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };

};

const singleOverlayMaxEdge = (): number => {

  const dpr = typeof window === 'undefined' ? 1 : (window.devicePixelRatio || 1);
  return Math.min(4096, Math.floor((GPU_MAX_TEXTURE - 1) / Math.max(1, dpr)));

};

const overlayMapMaxEdge = (visibleCount: number): number => {

  const single = singleOverlayMaxEdge();
  if (visibleCount <= 4) return single;
  if (visibleCount <= 10) return Math.max(512, Math.floor(single / 2));
  if (visibleCount <= 24) return Math.max(512, Math.floor(single / 4));
  return 512;

};

const overlayLatLngBounds = (points: Overlay['points'] | undefined): LatLngBounds | null => {

  if (!points || !isLatLng(points.nw) || !isLatLng(points.ne) || !isLatLng(points.sw) || !isLatLng(points.se)) {

    return null;

  }
  const bounds = L.latLngBounds(
    [points.nw.lat, points.nw.lng],
    [points.se.lat, points.se.lng],
  );
  bounds.extend([points.ne.lat, points.ne.lng]);
  bounds.extend([points.sw.lat, points.sw.lng]);
  return bounds;

};

const loadImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {

  const image = new Image();
  if (!url.startsWith('data:') && !url.startsWith('blob:')) image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('overlay image failed'));
  image.src = url;

});

const withDecodeSlot = <T>(work: () => Promise<T>): Promise<T> => new Promise((resolve, reject) => {

  const start = () => {

    activeDecodes += 1;
    work().then(resolve, reject).finally(() => {

      activeDecodes -= 1;
      decodeQueue.shift()?.();

    });

  };
  if (activeDecodes < MAX_PARALLEL_DECODES) start();
  else decodeQueue.push(start);

});

const resizeOverlayImage = async (url: string, maxEdge: number): Promise<string> => {

  const image = await loadImage(url);
  const size = fittedSize(image.naturalWidth, image.naturalHeight, maxEdge);
  if (!size) return url;

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) return SAFE_EMPTY_IMAGE;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, size.width, size.height);
  const blob = await new Promise<Blob | null>((resolve) => {

    canvas.toBlob((next) => resolve(next), 'image/png');

  });
  if (!blob) return SAFE_EMPTY_IMAGE;
  return URL.createObjectURL(blob);

};

const forgetCachedSize = (url: string, promise: Promise<string>) => {

  const current = sizedUrls.get(url);
  if (!current) return;
  const next = current.filter((entry) => entry.url !== promise);
  if (next.length) sizedUrls.set(url, next);
  else sizedUrls.delete(url);

};

export const cappedOverlayImageUrl = (url: string, maxEdge: number): Promise<string> => {

  const entries = sizedUrls.get(url) ?? [];
  const exact = entries.find((entry) => entry.edge === maxEdge);
  if (exact) return exact.url;

  const pending = (async () => {

    try {

      const larger = entries
        .filter((entry) => entry.edge > maxEdge)
        .sort((a, b) => a.edge - b.edge)[0];
      const cached = larger ? await larger.url : url;
      const source = cached === SAFE_EMPTY_IMAGE ? url : cached;
      return await withDecodeSlot(() => resizeOverlayImage(source, maxEdge));

    } catch {

      return SAFE_EMPTY_IMAGE;

    }

  })();

  pending.then((result) => {

    if (result === SAFE_EMPTY_IMAGE) forgetCachedSize(url, pending);

  });

  entries.push({ edge: maxEdge, url: pending });
  sizedUrls.set(url, entries);
  return pending;

};

const overlayCorners = (overlay: Overlay) => ([
  L.latLng(overlay.points.nw.lat, overlay.points.nw.lng),
  L.latLng(overlay.points.ne.lat, overlay.points.ne.lng),
  L.latLng(overlay.points.sw.lat, overlay.points.sw.lng),
  L.latLng(overlay.points.se.lat, overlay.points.se.lng),
]);

const removeDistortableLayer = (layer: any) => {

  try {

    if (layer?.editing) {

      const handles = layer.editing.currentHandle?._layers ?? {};
      Object.values(handles).forEach((handle: any) => handle.remove());
      layer.editing.currentHandle = null;

    }
    if (layer?._map) layer.remove();

  } catch {

    // The map unload handler may already have removed this layer.

  }

};

export const attachOverlayLayers = (map: LeafletMap, options: AttachOptions) => {

  const mounted = new Map<string, { layer: any; edge: number }>();
  const inflight = new Set<string>();
  const failed = new Set<string>();
  let wanted = new Map<string, number>();
  let stackOrder: string[] = [];
  let destroyed = false;

  const applyStackIndex = (layer: any, overlayId: string) => {

    const stackIndex = Math.max(0, stackOrder.indexOf(overlayId));
    layer.options._stackIndex = stackIndex;
    layer.setZIndex?.(stackIndex);

  };

  const addLayer = async (overlay: Overlay, edge: number) => {

    const source = overlayImageSource(overlay.imageUrl);
    const failureKey = `${overlay._id}|${edge}`;
    if (
      destroyed
      || !source
      || inflight.has(overlay._id)
      || mounted.get(overlay._id)?.edge === edge
      || failed.has(failureKey)
    ) return;

    inflight.add(overlay._id);
    try {

      const url = await cappedOverlayImageUrl(source, edge);
      if (destroyed || wanted.get(overlay._id) !== edge || mounted.get(overlay._id)?.edge === edge) return;

      const existing = mounted.get(overlay._id);
      const imageLayer = AnyL.distortableImageOverlay(url, {
        corners: existing?.layer.getCorners?.() ?? overlayCorners(overlay),
        editable: options.editable,
        selected: false,
        actions: options.actions,
        className: options.className,
        mode: options.mode,
        _id: overlay._id,
      });
      if (options.exposeFeatureId) {

        imageLayer.toGeoJSON = () => ({ id: overlay._id });

      }
      imageLayer.addTo(map);
      applyStackIndex(imageLayer, overlay._id);
      if (existing) removeDistortableLayer(existing.layer);
      mounted.set(overlay._id, { layer: imageLayer, edge });
      options.onCreate?.(imageLayer, overlay);

    } catch (error) {

      failed.add(failureKey);
      console.error('Error adding overlay to map', error);

    } finally {

      inflight.delete(overlay._id);
      const edgeNow = wanted.get(overlay._id);
      if (
        !destroyed
        && edgeNow !== undefined
        && mounted.get(overlay._id)?.edge !== edgeNow
        && !failed.has(`${overlay._id}|${edgeNow}`)
      ) {

        addLayer(overlay, edgeNow).catch(() => undefined);

      }

    }

  };

  const sync = (overlays: Overlay[], keepIds: string[] = []) => {

    if (destroyed) return;

    let view: LatLngBounds | null = null;
    try {

      view = map.getBounds().pad(0.25);

    } catch {

      view = null;

    }

    const keep = new Set(keepIds);
    const visible = overlays.filter((overlay) => {

      const bounds = overlayLatLngBounds(overlay.points);
      if (!overlayImageSource(overlay.imageUrl) || !bounds) return false;
      if (keep.has(overlay._id)) return true;
      return !!view && view.intersects(bounds);

    });
    const batchEdge = overlayMapMaxEdge(visible.length);
    const fullEdge = singleOverlayMaxEdge();
    stackOrder = visible.map((overlay) => overlay._id);
    wanted = new Map(visible.map((overlay) => [
      overlay._id,
      keep.has(overlay._id) ? fullEdge : batchEdge,
    ]));

    for (const [id, record] of [...mounted]) {

      if (wanted.has(id)) continue;
      removeDistortableLayer(record.layer);
      mounted.delete(id);

    }
    visible.forEach((overlay) => {

      const edge = wanted.get(overlay._id);
      const record = mounted.get(overlay._id);
      if (record && edge === record.edge) {

        record.layer.options._stackIndex = Math.max(0, stackOrder.indexOf(overlay._id));
        if (!keep.has(overlay._id)) record.layer.setZIndex?.(record.layer.options._stackIndex);
        return;

      }
      if (edge !== undefined) addLayer(overlay, edge).catch(() => undefined);

    });

  };

  const destroy = () => {

    destroyed = true;
    wanted = new Map();
    mounted.forEach((record) => removeDistortableLayer(record.layer));
    mounted.clear();

  };

  return { sync, destroy };

};
